import type { ReactNode } from "react";
import { Fragment } from "react";

import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { shortMonthShortDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { DEVICE_LABEL_KEYS } from "app/pages/digital-marketing/keywords-campaigns/shared/components/SerpOverlay/constants";
import { deviceIconMap } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";

const metadataTextProps = {
    fontVariant: "caption" as const,
    color: colorsPalettes.carbon[300],
};

type Props = {
    date: string;
    geoLocationText: string;
    device: LocationDevice;
};

export const SerpOverlayHeaderInfo = ({ date, geoLocationText, device }: Props) => {
    const { translate } = useTranslate();
    const formattedDate = dayjs(date).format(shortMonthShortDayAndYear);
    const deviceLabel = translate(DEVICE_LABEL_KEYS[device]);
    const DeviceIcon = deviceIconMap[device];

    const segments: ReactNode[] = [
        <Text
            key="date"
            {...metadataTextProps}
        >
            {formattedDate}
        </Text>,
        <Text
            key="location"
            {...metadataTextProps}
        >
            {geoLocationText}
        </Text>,
        <Flex
            key="device"
            alignItems="center"
            gap="4px"
            inline
        >
            <DeviceIcon
                size={12}
                color={colorsPalettes.carbon[300]}
            />
            <Text {...metadataTextProps}>{deviceLabel}</Text>
        </Flex>,
    ];

    return (
        <Flex
            data-automation="serp-overlay-header-info"
            alignItems="center"
            gap="4px"
            inline
        >
            {segments.map((segment, index) => (
                <Fragment key={index}>
                    {index > 0 && <Text {...metadataTextProps}>•</Text>}
                    {segment}
                </Fragment>
            ))}
        </Flex>
    );
};
